import dayjs from "dayjs";

export const DASHBOARD_PERIODS = {
  ALL_TIME: "all_time",
  LAST_MONTH: "last_month",
  LAST_12_MONTHS: "last_12_months",
  LAST_24_MONTHS: "last_24_months",
  YTD: "ytd",
  CUSTOM: "custom",
};

/** Earliest month bucket for All Time (Fiddux operational history). */
const ALL_TIME_START = dayjs("2020-01-01").startOf("month");

export const parseNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const parseDateToDayjs = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return dayjs(value.toDate());
  if (value instanceof Date) return dayjs(value);
  if (typeof value?.seconds === "number") {
    return dayjs(value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000));
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export const getMonthKey = (dateValue) => {
  const parsed = parseDateToDayjs(dateValue);
  if (!parsed?.isValid()) return null;
  return parsed.startOf("month").format("YYYY-MM");
};

export const getMonthLabel = (monthKey) => dayjs(`${monthKey}-01`).format("MMM YYYY");

export const getPeriodBounds = ({ period = DASHBOARD_PERIODS.LAST_12_MONTHS, startDate, endDate }) => {
  const now = dayjs();

  if (period === DASHBOARD_PERIODS.ALL_TIME) {
    return {
      start: ALL_TIME_START,
      end: now.endOf("day"),
      monthCount: Math.max(now.diff(ALL_TIME_START, "month") + 1, 1),
      isAllTime: true,
    };
  }

  if (period === DASHBOARD_PERIODS.LAST_MONTH) {
    const previousMonth = now.subtract(1, "month");
    return {
      start: previousMonth.startOf("month"),
      end: previousMonth.endOf("month"),
      monthCount: 1,
    };
  }

  if (period === DASHBOARD_PERIODS.CUSTOM && startDate && endDate) {
    return {
      start: dayjs(startDate).startOf("day"),
      end: dayjs(endDate).endOf("day"),
      monthCount: Math.max(dayjs(endDate).diff(dayjs(startDate), "month") + 1, 1),
    };
  }

  if (period === DASHBOARD_PERIODS.YTD) {
    return {
      start: now.startOf("year"),
      end: now.endOf("day"),
      monthCount: now.month() + 1,
    };
  }

  const monthCount = period === DASHBOARD_PERIODS.LAST_24_MONTHS ? 24 : 12;
  return {
    start: now.subtract(monthCount - 1, "month").startOf("month"),
    end: now.endOf("day"),
    monthCount,
  };
};

export const buildMonthBuckets = ({ start, end }) => {
  const buckets = [];
  let cursor = start.startOf("month");
  const lastMonth = end.startOf("month");

  while (cursor.isBefore(lastMonth) || cursor.isSame(lastMonth, "month")) {
    const key = cursor.format("YYYY-MM");
    buckets.push({
      key,
      label: cursor.format("MMM YYYY"),
      month: cursor.month() + 1,
      year: cursor.year(),
      deposits: 0,
      withdrawals: 0,
      netFlow: 0,
      individualDeposits: 0,
      individualWithdrawals: 0,
      individualNetFlow: 0,
      corporateDeposits: 0,
      corporateWithdrawals: 0,
      corporateNetFlow: 0,
      individualPerformance: 0,
      corporatePerformance: 0,
      performancePaid: 0,
      aum: 0,
      individualAum: 0,
      corporateAum: 0,
    });
    cursor = cursor.add(1, "month");
  }

  return buckets;
};

export const isWithinBounds = (dateValue, bounds) => {
  const parsed = parseDateToDayjs(dateValue);
  if (!parsed?.isValid()) return false;
  return (
    (parsed.isAfter(bounds.start) || parsed.isSame(bounds.start)) &&
    (parsed.isBefore(bounds.end) || parsed.isSame(bounds.end))
  );
};

export const getDeltaPercent = (current, previous) => {
  if (!previous) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};
